import { useCallback, useRef } from 'react';

import type { SanityTwoUpForm } from '#/lib/sanity/queries/twoUpForm';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
} from '#/lib/section-theme';
import { cn } from '#/lib/utils';

import { FormCustom } from './Form/Custom';
import { getHeadlineHighlightClass, Headline } from './Headline';
import { Media } from './Media';
import { Overline } from './Overline';
import { RichText } from './RichText';

/**
 * Form theming notes (same as Hero Form):
 * - light → light inputs
 * - charcoal → dark inputs
 * - purple → light inputs on the purple surface (dark inputs are tuned for
 *   charcoal, not cipher, until dedicated purple form styles exist)
 */
export function TwoUpForm(props: SanityTwoUpForm) {
  const hasHeadingAbove = props.display === 'headingAbove';
  const hasFormattedBodyCopy = Boolean(
    props.formattedBodyCopy && props?.formattedBodyCopy?.length,
  );
  const isFirst = props.sectionIndex === 0;
  // A legacy background color takes precedence over the theme; without one,
  // the theme drives the section's colors via the shared surface map.
  const theme = props.background ? 'light' : props.theme;
  const isDark = isDarkSectionTheme(theme);
  const highlightClass = isDark
    ? getSectionThemeAccent(theme)
    : (getHeadlineHighlightClass({ background: props.background }) ??
      getSectionThemeAccent(theme));
  const formTheme = theme === 'charcoal' ? 'dark' : 'light';
  const bodyTextClass = isDark ? 'text-body-cloud' : 'text-body';
  const ref = useRef<HTMLDivElement>(null);

  const desktopFormClassNames = {
    right: {
      form: 'l:ml-auto',
      container: 'l:order-last',
    },
    left: {
      form: 'l:mr-auto',
      container: 'l:order-first',
    },
  }[props.desktopFormPosition];

  const scrollIntoView = useCallback(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ref]);

  const Heading = (
    <Headline
      value={props.headline}
      overline={props.overline}
      overlineClassName={cn('type-eyebrow', highlightClass)}
      className="mb-25"
      variant={props.headlineSize === 'large' ? 2 : 4}
      highlightClass={highlightClass}
    />
  );

  const BodyContent = (
    <>
      {props.bodyCopy && !hasFormattedBodyCopy ? (
        <p className={cn('max-w-480 text-pretty', bodyTextClass)}>
          {props.bodyCopy}
        </p>
      ) : null}
      {hasFormattedBodyCopy ? (
        <RichText
          value={props.formattedBodyCopy ?? []}
          normalTextSizeClassName={bodyTextClass}
        />
      ) : null}
    </>
  );

  return (
    <div
      ref={ref}
      className={cn('p-main', getSectionThemeSurface(theme), {
        'py-100': !isFirst,
        'mt-[calc(var(--header-height)*-1)] pb-60 l:pb-100 pt-[calc(var(--header-height)+5rem)]':
          isFirst,
      })}
      style={{
        background: props.background ?? undefined,
      }}
    >
      <div className="fade-in grid-main mx-auto max-w-600 l:max-w-1235">
        {!hasHeadingAbove && (
          <div
            className={cn(
              'col-span-full l:row-start-1 l:col-span-6',
              props.desktopFormPosition === 'right'
                ? 'l:col-start-1'
                : 'l:col-start-7',
            )}
          >
            {Heading}
            {BodyContent}
          </div>
        )}
        <div
          className={cn(
            desktopFormClassNames.container,
            'col-span-full row-span-3 pt-16 l:col-span-6',
            {
              'pt-0': hasHeadingAbove,
            },
          )}
        >
          <div className={cn(desktopFormClassNames.form, 'l:max-w-480')}>
            {hasHeadingAbove && (
              <div className="mb-20">
                {Heading}
                {BodyContent}
              </div>
            )}
            <FormCustom
              className={cn(
                // Privacy + step label must stay readable on purple/charcoal
                // even when inputs stay light.
                isDark &&
                  '[&_.legal-text]:!text-cloud [&_.step-indicator]:!text-cloud',
              )}
              form={props.form}
              confirmation={props.confirmation}
              theme={formTheme}
              buttonTheme={getSectionButtonTheme(theme)}
              onSubmitSuccess={scrollIntoView}
              onSubmitError={scrollIntoView}
            />
          </div>
        </div>
        <div
          className={cn(
            'col-span-full l:col-span-6',
            desktopFormClassNames.container,
          )}
        >
          {props.additionalContent?.map((section) => {
            switch (section._type) {
              case 'image':
              case 'videoLoop':
                return (
                  <div
                    className={cn('mt-40', {
                      'l:mt-0': hasHeadingAbove,
                    })}
                  >
                    <div className="mx-auto max-w-550  px-0 l:mx-0">
                      <Media
                        key={section._key}
                        media={section}
                        sizes={['600px', { l: '50vw', '1285px': '620px' }]}
                      />
                    </div>
                  </div>
                );
              case 'logoWall':
                return (
                  <div
                    key={section._key}
                    className="mt-60 text-center l:text-left"
                  >
                    {section.overline ? (
                      <Overline className={cn('mb-30', highlightClass)}>
                        {section.overline}
                      </Overline>
                    ) : null}
                    <div className="l:justify-left flex flex-wrap items-center justify-center gap-x-20 gap-y-16 l:gap-x-36 l:gap-y-28">
                      {section.logos.map((item) => (
                        <Media
                          key={item._key}
                          media={item.logo}
                          className="object-contain"
                          style={{ width: item.logoWidth }}
                          sizes={`${item.logoWidth}px`}
                        />
                      ))}
                    </div>
                  </div>
                );
            }
          })}
        </div>
      </div>
    </div>
  );
}
